"use client";

import { useSyncExternalStore } from "react";

// "Has React finished hydrating?" is a fact owned by React's own render
// pipeline, not derived from props — so it is read as an external store with a
// server snapshot of `false` and a client snapshot of `true`. The subscribe
// callback never emits because the value flips exactly once, when React swaps
// the server snapshot for the client one after hydration.
const subscribeToHydration = () => () => {};
const getHydratedSnapshot = () => true;
const getHydratedServerSnapshot = () => false;

/**
 * `false` through the server render and the hydration pass, `true` on every
 * render after. The gate for anything that must not appear in the markup React
 * has to match: Convex hooks before the provider is wired, `createPortal`
 * before `document.body` exists, a `next-themes` value, a client-only clock.
 *
 * Twelve modules used to declare this read themselves — eleven of them a
 * module-scope triple of a no-op subscribe, a `() => true` client snapshot and
 * a `() => false` server snapshot, the twelfth writing the two snapshots inline
 * at the call. Thirty-three constants under seven naming schemes
 * (`subscribeToHydration` / `subscribeToNothing` / `subscribeToMounted` /
 * `subscribeToPortalMounted`, `getHydratedSnapshot` / `getClientSnapshot` /
 * `getMountedSnapshot` / `getHydratedOnClient`) all spelled the same mechanism,
 * and three of the sites said so in their own comments — one "mirrors the
 * pattern used by", one "same pattern as LastSeenTracker", one "same shape as
 * AnimatedThemeToggle and AssistantRuntime" — while a fourth had already
 * wrapped its triple in a private `usePortalMounted`. The drift was never in
 * the value, which is identical at every site; it was in the names and in the
 * five-line mechanism paragraph, which had been re-derived in four different
 * wordings and copy-pasted verbatim in two others.
 *
 * This module now owns the mechanism: the snapshot functions, their module
 * scope (which is what keeps snapshot identity and subscribe stability across
 * renders), and the paragraph explaining why an external store and not a mount
 * effect. What stays with each caller is the consequence — which branch the
 * boolean feeds, what placeholder renders while it is `false`, and the comment
 * naming the specific hazard that made that caller gate.
 */
export function useHydrated(): boolean {
  return useSyncExternalStore(subscribeToHydration, getHydratedSnapshot, getHydratedServerSnapshot);
}
